import { ArrowTopRightIcon, CornerBottomLeftIcon } from "@radix-ui/react-icons";
import { Badge, Flex, Grid, Link, Text } from "@radix-ui/themes";
import { FC, useEffect, useState } from "react";
import { GetOrderResponseType } from "../../api";
import {
  Input,
  InputProps,
  ProgressBar,
  Select,
  SelectProps,
  Table,
  TableCell,
  TableProps,
  TableRow,
} from "../../components";
import { getDateFromString } from "../../functions";
import { Details } from "./components/details";
import { ImageModal } from "./components/imageModal";
import { ShippingAddress } from "./components/shippingAddress";
import { getMaxStep, getSimpleStateSelectOptions } from "./deliveryState";
import "./index.css";
import {
  ExpandedTrackingItemType,
  filterTrackingItems,
  getBadgeColor,
  MappedTrackingItemsType,
  trackingItemsMapper,
} from "./utils";

type TrackingDetailProps = Pick<
  GetOrderResponseType,
  | "orderPos"
  | "shippingAddress"
  | "orderID"
  | "orderDate"
  | "dateExpected"
  | "shippingMethodID"
>;

const HEADER = [
  "Artikelnummer",
  "Artikel",
  "Menge",
  "Status",
  "Auftragsfortschritt",
  "Voraussichtlicher Liefertermin *",
  "Statusbeschreibung",
  "Sendungsverfolgung",
];

const SELECT_ITEMS = getSimpleStateSelectOptions();

export const TrackingDetail: FC<TrackingDetailProps> = (props) => {
  const {
    orderPos,
    shippingAddress,
    orderID,
    orderDate,
    dateExpected,
    shippingMethodID,
  } = props;
  const [mappedPositions, setMappedPositions] =
    useState<MappedTrackingItemsType>([]);
  const [filteredPositions, setFilteredPositions] =
    useState<MappedTrackingItemsType>([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [badgeFilter, setBadgeFilter] = useState("");

  useEffect(() => {
    setSearchFilter("");
    setBadgeFilter("");
    setMappedPositions(trackingItemsMapper(orderPos, shippingMethodID));
  }, [orderPos, shippingMethodID]);

  useEffect(() => {
    setFilteredPositions(
      filterTrackingItems(mappedPositions, {
        searchFilter,
        badgeFilter,
      }),
    );
  }, [searchFilter, badgeFilter, mappedPositions]);

  const onSearch: InputProps["onChange"] = (value) => {
    setSearchFilter(value);
  };

  const onBadgeFilterChange: SelectProps["onChange"] = (value: string) => {
    setBadgeFilter(value === SELECT_ITEMS[0].value ? "" : value);
  };

  const maxSteps = getMaxStep(shippingMethodID);

  const tableBodyRenderer: TableProps<ExpandedTrackingItemType>["tableBodyRenderer"] =
    (item, index) => {
      return (
        <TableRow key={`${index}-${item.articleNo}`}>
          <TableCell>
            <div className="table-cell-container">
              {item.level > 0 && (
                <div className="table-cell-level">
                  <CornerBottomLeftIcon />
                </div>
              )}
              {item.articleNo}
            </div>
          </TableCell>
          <TableCell>
            <b>{item.itemID}</b> | {item.articleName}
          </TableCell>
          <TableCell>
            <Text wrap="nowrap">
              {item.amountDelivered} / {item.amountOrdered} {item.salesUnit}
            </Text>
          </TableCell>
          <TableCell>
            <Badge color={getBadgeColor(item.badgeText)}>
              {item.badgeText}
            </Badge>
          </TableCell>
          <TableCell>
            <ProgressBar steps={maxSteps} currentStep={item.step} />
            <div>{item.simpleState}</div>
            {item.deliveryNoteNumber && (
              <div>(Lieferschein: {item.deliveryNoteNumber})</div>
            )}
          </TableCell>
          <TableCell>
            {item.step < maxSteps &&
              getDateFromString(item.displayDeliveryDate)}
          </TableCell>
          <TableCell>
            <div style={{ whiteSpace: "pre-line" }}>{item.infoText}</div>
          </TableCell>
          <TableCell>
            {item.habblImageURL && <ImageModal img={item.habblImageURL} />}
            {item.habblGeoCoords && (
              <Link href={item.habblGeoCoords} target="_blank">
                <Flex align="center">
                  Zu Maps <ArrowTopRightIcon />
                </Flex>
              </Link>
            )}
            {item.trackingURL && (
              <Link href={item.trackingURL} target="_blank">
                <Flex align="center">
                  Zum Tracking <ArrowTopRightIcon />
                </Flex>
              </Link>
            )}
          </TableCell>
        </TableRow>
      );
    };

  return (
    <div>
      <Grid mb="7" gap="2" columns={{ md: "2" }}>
        <ShippingAddress shippingAddress={shippingAddress} />
        <Details
          orderID={orderID}
          orderDate={orderDate}
          dateExpected={dateExpected}
        />
      </Grid>
      <Grid gap="2" columns={{ sm: "2" }}>
        <Grid columns={{ md: "2" }} gap="2" mb="1">
          <Text as="label">
            Suche:
            <Input
              placeholder="Artikelnummer / Artikel"
              onChange={onSearch}
              value={searchFilter}
              clearable
            />
          </Text>
          <Grid>
            <Text as="div">Status:</Text>
            <Select
              items={SELECT_ITEMS}
              defaultValue={SELECT_ITEMS[0].value}
              onChange={onBadgeFilterChange}
            />
          </Grid>
        </Grid>
      </Grid>

      <Table<ExpandedTrackingItemType>
        items={filteredPositions}
        header={HEADER}
        tableBodyRenderer={tableBodyRenderer}
      />
      {filteredPositions.length === 0 && <div>Keine Artikel gefunden.</div>}
      <Text as="p" className="tracking-disclaimer">
        *voraussichtlicher, unverbindlicher Liefer-, Abhol- oder Wunschtermin.
      </Text>
    </div>
  );
};
